import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Tabs, Button, Spin } from 'antd';
import { useLocation, useParams } from 'react-router-dom';
import moment from 'moment';
import { get } from 'lodash';
import {
  getCheckRoomList,
  getOrderCustomerInfo,
  getOrderDetailBaseInfo,
  getOrderRoomInfo,
  getStoreIsStartUseCard,
  queryIsChangeMaster,
} from '@/redux/actions/order';
import { getBusinessPaymentType } from '@/redux/actions/guest';
import { IorderBaseInfo } from '@/redux/reducers/order';
import { ItabItem, renderIntlLabel } from '@/utils';
import { HistoryParams } from '@/utils/type';
import { MAIN_ORDER_STATUS_MAP, ORDER_BILL_STATE_MAP, OrderType } from '@/config/constant';
import { DATE_TIME_FORMAT } from '@/config';
import OrderShareButton from '@/components/Order/ShareButton';
import useDetailDetector from '@/components/Order/DetailPortalDetect';
import { getTimeByUtcOffset } from '@/utils/intl';
import Bill from '../Bill';
import Room from '../Room';
import Guest from '../Guest';
import CheckRoom from '../CheckRoom';
import ActionLog from '../ActionLog';
import RentLend from '../RentLend';
import style from './index.module.scss';

const { TabPane } = Tabs;

export enum OrderDetailTabKey {
  GuestKey = 'guest',
  RoomKey = 'room',
  BillKey = 'bill',
  CheckRoomKey = 'checkRoom',
  ActionLogKey = 'actionLog',
  RentLendKey = 'rentLend',
}

const tabList: Array<ItabItem> = [
  {
    key: OrderDetailTabKey.GuestKey,
    tab: renderIntlLabel('order_frontDesk_detail_tab_guest'),
    component: <Guest />,
  },
  {
    key: OrderDetailTabKey.RoomKey,
    tab: renderIntlLabel('order_frontDesk_detail_tab_room'),
    component: <Room />,
  },
  {
    key: OrderDetailTabKey.BillKey,
    tab: renderIntlLabel('order_frontDesk_detail_tab_bill'),
    component: <Bill />,
  },
  {
    key: OrderDetailTabKey.CheckRoomKey,
    tab: renderIntlLabel('order_frontDesk_detail_tab_checkroom'),
    component: <CheckRoom />,
  },
  {
    key: OrderDetailTabKey.RentLendKey,
    tab: '租借',
    component: <RentLend />,
  },
  {
    key: OrderDetailTabKey.ActionLogKey,
    tab: renderIntlLabel('channel_log'),
    component: <ActionLog />,
  },
];

const mapFnToProps = {
  getBusinessPaymentTypeFn: getBusinessPaymentType,
  queryIsChangeMasterFn: queryIsChangeMaster,
  getOrderDetailBaseInfoFn: getOrderDetailBaseInfo,
  getOrderCustomerInfoFn: getOrderCustomerInfo,
  getOrderRoomInfoFn: getOrderRoomInfo,
  getCheckRoomListFn: getCheckRoomList,
  getStoreIsStartUseCardFn: getStoreIsStartUseCard,
};

type TMapFnToProps = typeof mapFnToProps;
type TDetailInfo = TMapFnToProps & { orderBaseInfo: IorderBaseInfo; onBack: () => void };

const DetailInfo: React.FC<Partial<TDetailInfo>> = ({
  orderBaseInfo,
  onBack,
  getBusinessPaymentTypeFn,
  getOrderDetailBaseInfoFn,
  getOrderCustomerInfoFn,
  queryIsChangeMasterFn,
  getOrderRoomInfoFn,
  getCheckRoomListFn,
  getStoreIsStartUseCardFn,
}) => {
  // 默认选中的tab选项
  const location = useLocation<{ tabKey: string }>();
  // 主单(预订单编号), 接待单状态
  const { resvNo } = useParams<HistoryParams>();
  const [isWaiting, setWaitingStatus] = useState(true);
  const [selectedKey, setSelectedKey] = useState<string>();
  const { runDetailDetector } = useDetailDetector();

  const handleBack = () => {
    if (typeof onBack === 'function') {
      onBack();
    }
  };

  const getDetailByTabKey = (val) => {
    if (val === OrderDetailTabKey.GuestKey) {
      getOrderDetailBaseInfoFn({ resvNo });
      getBusinessPaymentTypeFn();
      getOrderCustomerInfoFn({ resvNo });
      queryIsChangeMasterFn({ resvNo });
    }
    if (val === OrderDetailTabKey.RoomKey) {
      getOrderDetailBaseInfoFn({ resvNo });
      getOrderRoomInfoFn({ resvNo });
      // 房卡启用
      getStoreIsStartUseCardFn();
    }
    if (val === OrderDetailTabKey.CheckRoomKey) {
      getOrderDetailBaseInfoFn({ resvNo });
      getCheckRoomListFn({ resvNo });
    }
  };

  const handleTabChange = (val) => {
    setSelectedKey(val);
    getDetailByTabKey(val);
  };

  useEffect(() => {
    // 订单所属门店校验
    runDetailDetector(OrderType.roomBillOrder).then((success) => {
      if (!success) return;
      setWaitingStatus(false);

      const tabKey = get(location, 'state.tabKey');
      setSelectedKey(tabKey || OrderDetailTabKey.GuestKey);
      getDetailByTabKey(tabKey || OrderDetailTabKey.GuestKey);
    });
  }, [location]);

  return (
    <>
      <div className={style.container}>
        <>
          <Spin spinning={isWaiting}>
            <div className={style.header}>
              <div className={style.descriptions}>
                <div>
                  {renderIntlLabel('order_detail_room_order_no')}：{orderBaseInfo.resvNo}
                </div>
                <div>
                  {renderIntlLabel('order_detail_order_create_time')}：
                  {orderBaseInfo.createTime
                    ? getTimeByUtcOffset(orderBaseInfo.createTime, DATE_TIME_FORMAT)
                    : ''}
                </div>
                <div>
                  {renderIntlLabel('order_detail_order_status')}：
                  {MAIN_ORDER_STATUS_MAP[orderBaseInfo.resvStatus]}
                </div>
                <div>
                  {renderIntlLabel('order_detail_bill_status')}：
                  {ORDER_BILL_STATE_MAP[orderBaseInfo.newAccountingStatus] || '-'}
                </div>
              </div>
              <div className="flex">
                <Button onClick={handleBack} style={{ marginRight: 16 }}>
                  {renderIntlLabel('order_action_return')}
                </Button>
                <OrderShareButton />
              </div>
            </div>
          </Spin>

          {!isWaiting && (
            <Tabs
              activeKey={selectedKey}
              onChange={handleTabChange}
              type="card"
              destroyInactiveTabPane
              style={{ marginTop: '32px' }}
            >
              {Array.isArray(tabList)
                ? tabList.map((item) => (
                    <TabPane tab={item.tab} key={item.key}>
                      {item.component}
                    </TabPane>
                  ))
                : null}
            </Tabs>
          )}
        </>
      </div>
    </>
  );
};

const mapStateToProps = (state) => {
  return {
    ...state.order,
  };
};

export default connect(mapStateToProps, mapFnToProps)(React.memo(DetailInfo));
